import { useState, useCallback } from "react"

export interface UseDragAndDropProps {
  onFilesDropped?: (files: File[]) => void
  onDragStateChange?: (isDragging: boolean) => void
  logPrefix?: string
}

export interface UseDragAndDropResult {
  isDragging: boolean
  dragHandlers: {
    onDragOver: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
}

/**
 * Custom hook to handle drag and drop functionality
 * @param onFilesDropped Callback when files are dropped
 * @param onDragStateChange Callback when drag state changes
 * @param logPrefix Prefix for console logs
 * @returns Object containing isDragging state and event handlers
 */
export const useDragAndDrop = ({
  onFilesDropped,
  onDragStateChange,
  logPrefix = "DragAndDrop",
}: UseDragAndDropProps = {}): UseDragAndDropResult => {
  const [isDragging, setIsDragging] = useState(false)

  const updateDraggingState = useCallback(
    (dragging: boolean) => {
      if (isDragging !== dragging) {
        setIsDragging(dragging)
        onDragStateChange?.(dragging)
      }
    },
    [isDragging, onDragStateChange]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      updateDraggingState(true)
    },
    [updateDraggingState]
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      // Only update state if we're leaving the target element, not entering a child
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        updateDraggingState(false)
      }
    },
    [updateDraggingState]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation() // Prevent event bubbling
      updateDraggingState(false)

      const droppedFiles = Array.from(e.dataTransfer.files)
      if (droppedFiles.length > 0) {
        console.log(
          `${logPrefix} - Dropped files:`,
          droppedFiles.map((f) => f.name)
        )
        onFilesDropped?.(droppedFiles)
      } else {
        console.log(`${logPrefix} - No files were dropped`)
      }
    },
    [updateDraggingState, onFilesDropped, logPrefix]
  )

  return {
    isDragging,
    dragHandlers: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  }
}

export default useDragAndDrop
